import { Injectable, inject } from '@angular/core';
import { downgradeInjectable } from '@angular/upgrade/static';
import App from '@app/app';
import type { IShoppingCartStore } from '@app/interface/cart/shopping-cart-store.interface';
import type { IJobStore } from '@app/interface/job/job-store.interface';
import type { UserProfileStoreService } from '@app/interface/service/user-profile-store-service.interface';
import { FeatureFlagService } from '@app/service/feature-flag.service';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';
import { BannerType } from '@app/components/student-banner/student-banner.component';
import { parseDateComponents } from '@app/utils/dates';

@Injectable({
  providedIn: 'root'
})
export class StudentBannerService {
  private readonly featureFlagService = inject(FeatureFlagService);
  private shoppingCartStore: IShoppingCartStore = inject(angularjs.ShoppingCartStore);
  private readonly jobStore: IJobStore = inject(angularjs.JobStore);
  private readonly userProfileStore: UserProfileStoreService = inject(angularjs.UserProfileStore);

  public isStudentBannerEnabled(): boolean {
    return this.featureFlagService.isEnabled('OPTIMIZEPREPIC');
  }

  public getSubProgramDisplay(jobName: string): string {
    if (!jobName) return '';

    const lower = jobName.toLowerCase();

    if (lower.includes('groups')) return 'Groups';
    if (lower.includes('grads')) return 'Grads';

    const firstWord = jobName.trim().split(/\s+/)[0];
    return firstWord.charAt(0).toUpperCase() + firstWord.slice(1).toLowerCase();
  }

  public getEventNameAndYear(jobName: string, eventDate: string, gradYear?: number): string {
    const subProgram = this.getSubProgramDisplay(jobName);
    const useGradYear = gradYear !== undefined && jobName.toLowerCase().includes('grads');
    const year = useGradYear ? gradYear : eventDate ? parseDateComponents(eventDate)[0] : '';
    return `${subProgram} ${year}`;
  }

  public getBannerType(): BannerType {
    if (!this.isStudentBannerEnabled()) return BannerType.HIDDEN;

    const PREORDER_ORDER_TYPE = 'PREORDER';
    const currentSubject = this.shoppingCartStore.getCurrentSubject();
    const displayName = currentSubject?.subjectFirstName ?? '';
    const resolvedJobKey = currentSubject?.portraitDashboard?.jobKey;
    const subjectDetailsDeferred = currentSubject?.subjectDetailsDeferred;

    if (!displayName.trim() || subjectDetailsDeferred) {
      return BannerType.PRE_PIC_DAY;
    }

    const job = this.jobStore.getJobByJobKey(resolvedJobKey);

    const isPreorder = job?.orderType === PREORDER_ORDER_TYPE;

    const isLoggedIn = this.userProfileStore.isAuthenticated();

    if (!isLoggedIn && isPreorder) {
      return BannerType.PRE_PIC_DAY;
    }

    return BannerType.DEFAULT;
  }
}

App.factory('StudentBannerService', downgradeInjectable(StudentBannerService));
